import React from 'react';
import { Leaf, Wind, Filter, TrendingDown, Truck, ChevronsDown } from 'lucide-react';

/**
 * Компонент с настройками эко-режима для оптимизации расхода топлива
 */
const EcoModesSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  if (!formData.includeEcoInfo) {
    return (
      <div className="mb-6">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeEcoInfo"
            checked={formData.includeEcoInfo}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать эко-режимы и способы снижения расхода
          </span>
        </label>
      </div>
    );
  }

  // Данные о способах экономии топлива
  const ecoMethods = [
    {
      id: 'tireInflation',
      name: 'Правильное давление в шинах',
      description: 'Недокачанные шины увеличивают сопротивление качению',
      icon: <ChevronsDown className="w-5 h-5 text-green-500" />,
      savings: '2-3%'
    },
    {
      id: 'airFilter',
      name: 'Чистый воздушный фильтр',
      description: 'Загрязненный фильтр ухудшает сгорание топлива',
      icon: <Filter className="w-5 h-5 text-green-500" />,
      savings: '3-5%'
    },
    {
      id: 'airConditioner',
      name: 'Рациональное использование кондиционера',
      description: 'Кондиционер потребляет дополнительную мощность двигателя',
      icon: <Wind className="w-5 h-5 text-green-500" />,
      savings: '5-10%'
    },
    {
      id: 'cargoWeight',
      name: 'Оптимизация веса и багажа',
      description: 'Лишний вес увеличивает расход топлива',
      icon: <Truck className="w-5 h-5 text-green-500" />,
      savings: '1-2%'
    },
    {
      id: 'economicDriving',
      name: 'Экономичный стиль вождения',
      description: 'Плавное ускорение, торможение двигателем и оптимальные обороты',
      icon: <TrendingDown className="w-5 h-5 text-green-500" />,
      savings: '10-15%'
    }
  ];

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeEcoInfo"
            checked={formData.includeEcoInfo}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white flex items-center">
            <Leaf className="w-5 h-5 mr-1 text-green-500" />
            Эко-режимы и экономия топлива
          </span>
        </label>
      </div>

      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
        <p className="text-sm text-green-700 dark:text-green-300 mb-4">
          Выберите способы экономии топлива, которые вы используете или планируете использовать:
        </p>
        
        <div className="space-y-3">
          {ecoMethods.map((method) => (
            <div 
              key={method.id} 
              className={`p-3 rounded-lg border transition-colors cursor-pointer
                ${formData.ecoMode[method.id] 
                  ? 'bg-white dark:bg-gray-800 border-green-300 dark:border-green-700' 
                  : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600'}`}
              onClick={() => {
                handleInputChange({
                  target: {
                    type: 'checkbox',
                    name: `ecoMode.${method.id}`,
                    checked: !formData.ecoMode[method.id]
                  }
                });
              }}
            >
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id={`ecoMode.${method.id}`}
                  name={`ecoMode.${method.id}`}
                  checked={formData.ecoMode[method.id]}
                  onChange={handleInputChange}
                  className="form-checkbox mt-1"
                />
                <div className="ml-3">
                  <label 
                    htmlFor={`ecoMode.${method.id}`} 
                    className="font-medium text-sm text-gray-800 dark:text-gray-200 flex items-center"
                  >
                    {method.icon}
                    <span className="ml-1">{method.name}</span>
                    <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
                      (экономия {method.savings})
                    </span>
                  </label>
                  <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
                    {method.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-green-200 dark:border-green-700">
          <div className="flex items-start">
            <Leaf className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                Влияние на расчет
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Выбранные способы экономии будут учтены при расчете расхода топлива. Общая экономия от всех мер 
                не может превышать 30% от базового расхода. Калькулятор автоматически рассчитает оптимальный 
                результат с учетом выбранных вами мер.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcoModesSection;